import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { getUserFullName } from 'utils';
import {
  DataRoomRFI,
  DataRoomRFIReport,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useRfiSendUploaderReminder from 'router/subrouters/DataRoom/hooks/useRfiSendUploaderReminder';
import usePastTime from 'hooks/usePastTime';
import ResolvePopup from 'components/ResolvePopup';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import { getIsRfiStatusClosed } from 'router/subrouters/DataRoom/utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';

import './RfiAnalyticsUploaderInstance.styles.scss';

type RfiAnalyticsUploaderInstanceProps = {
  className?: string;
  rfi: DataRoomRFI;
  report: DataRoomRFIReport;
  updateReport: (report: DataRoomRFIReport) => void;
};

const RfiAnalyticsUploaderInstance: React.FC<
  RfiAnalyticsUploaderInstanceProps
> = (props) => {
  const { className, report, rfi, updateReport } = props;

  const { _id, allRecipientsWithReminders, status } = rfi;

  const {
    dataRoomRfiFileUploader,
    remindersSentNumber,
    remindersSentDates,
    timesOpenedNumber,
    rfiOpenedDates,
    filesUploadedNumber,
    filesUploaded,
  } = report;

  const isRfiClosed = getIsRfiStatusClosed(status);

  const { updateRfi } = useContext(RfiContext);
  const { doesntHaveAccess } = useContext(DataRoomContext);

  const { isLoading: isLoadingReminder, onSendReminder } =
    useRfiSendUploaderReminder(
      _id,
      allRecipientsWithReminders,
      (allRecipientsWithReminders) => {
        updateRfi({ ...rfi, allRecipientsWithReminders });
        updateReport({
          ...report,
          remindersSentNumber: remindersSentNumber + 1,
          remindersSentDates: [...remindersSentDates, new Date()],
        });
      },
    );

  const classes = classNames(
    'rfi-analytics-uploader-instance',
    'grid',
    className,
  );

  const { t } = useTranslation();

  const pastTime = usePastTime();

  const renderDatePopupContent = useCallback(
    (value: number, dates?: Date[]) =>
      dates?.length ? (
        <ResolvePopup
          onHover
          popupContent={
            <ol className="resolve-popup-ol">
              {dates.map((date) => (
                <li key={+new Date(date)}>{pastTime(date)}</li>
              ))}
            </ol>
          }
        >
          {value}
        </ResolvePopup>
      ) : (
        value
      ),
    [pastTime],
  );

  const renderTextPopupContent = useCallback(
    (value: number, text?: string[]) =>
      text?.length ? (
        <ResolvePopup
          onHover
          popupContent={
            <ol className="resolve-popup-ol">
              {text.map((el) => (
                <li key={el}>{el}</li>
              ))}
            </ol>
          }
        >
          {value}
        </ResolvePopup>
      ) : (
        value
      ),
    [],
  );

  return (
    <div className={classes}>
      <p>
        {getUserFullName(
          dataRoomRfiFileUploader.firstName,
          dataRoomRfiFileUploader.lastName,
        )}
      </p>
      <p className="uploaded-files">
        {renderTextPopupContent(filesUploadedNumber, filesUploaded)}
      </p>
      <p className="times-opened">
        {renderDatePopupContent(timesOpenedNumber, rfiOpenedDates)}
      </p>
      <p className="reminders-sent">
        {renderDatePopupContent(remindersSentNumber, remindersSentDates)}
      </p>
      {!isRfiClosed && !doesntHaveAccess && (
        <Button
          variant="outline"
          disabled={isLoadingReminder}
          onClick={() => onSendReminder(dataRoomRfiFileUploader.email)}
        >
          {t('sendReminder')}
        </Button>
      )}
    </div>
  );
};

export default RfiAnalyticsUploaderInstance;
